import { Directive, ElementRef, input } from '@angular/core';

/**
 * @description
 * When placed on an HTML element, this directive enables programmatic smooth scrolling
 * to the bottom of the nearest scrollable ancestor container.
 *
 * @example
 * <div elementScroller [elementScrollerId]="'my-scroller'">...</div>
 */
@Directive({
  selector: '[elementScroller]',
  standalone: true
})
export class ElementScrollerDirective {
  /**
   * @description
   * Unique identifier used by {@link ElementScrollerMediatorDirective} to match this scroller instance.
   */
  public readonly elementScrollerId = input.required<string>();

  constructor(private el: ElementRef<HTMLElement>) {}

  /**
   * @description
   * Smoothly scrolls the nearest scrollable ancestor container to its bottom.
   */
  scrollToElementBottom() {
    if(!this.el) return;
    
    const container = this.getScrollParent(this.el.nativeElement);

    if(!container) return;

    container.scrollTo({
      top: container.scrollHeight,
      behavior: 'smooth'
    });
  }

  /**
   * @description
   * Recursively traverses the DOM upward from the given node to find the nearest
   * ancestor that has overflowing content.
   * Falls back to `document.documentElement` if no scrollable parent is found.
   *
   * @param node - The starting HTML element from which to search upward.
   * @returns The nearest scrollable ancestor element.
   */
  private getScrollParent(node: HTMLElement | null): HTMLElement {
    if (!node || node === document.body) {
      return document.documentElement;
    }

    const overflowY = window.getComputedStyle(node).overflowY;
    const isScrollable = overflowY === 'auto' || overflowY === 'scroll';

    if (isScrollable && node.scrollHeight > node.clientHeight) {
      return node;
    }

    return this.getScrollParent(node.parentElement);
  }
}
